/**
 * InlineCustomFieldEditor — Click-to-edit custom field on member demographics
 * Saves only the single changed field via lightweight API call (avoids 502 timeout
 * that occurs when the full member edit form sends all fields at once).
 */

import React, { useContext, useState } from 'react';
import TranslationContext from '@/contexts/TranslationContext';
import { Check, X, Pencil, Clock, Loader2, Pin, PinOff, Settings, Calculator, AlertTriangle } from 'lucide-react';
import type { CustomField, FieldOption } from '@/services/api/members';
import { describeRule, formatDerived, isDerived } from '@/utils/derivedFields';
import { orderOptions } from '@/utils/optionOrder';
import { isOffList, needsReassignment } from '@/utils/reassignment';

import { config as productConfig } from '@/src/config';
import { autoGrowTextarea, longTextRows } from '@/lib/longTextSizing';
interface Props {
  field: CustomField;
  value: any;
  memberId: number;
  onSave: (fieldName: string, newValue: string) => Promise<boolean>;
  /** The record's other custom field values, for the field's reassign_rules (TRUS-716). */
  recordValues?: Record<string, any>;
}

export default function InlineCustomFieldEditor({ field, value, memberId, onSave, recordValues }: Props) {
  // Optional context (like linkify): several tests render this editor without a
  // TranslationProvider, so fall back to the English text there.
  const i18n = useContext(TranslationContext);
  const t = (key: string, fallback: string, opts?: Record<string, unknown>): string =>
    i18n ? i18n.t(key, fallback, opts) : fallback.replace(/\{\{(\w+)\}\}/g, (_m, k) => String(opts?.[k] ?? ''));
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(String(value ?? ''));
  const [saving, setSaving] = useState(false);
  const [displayValue, setDisplayValue] = useState(value);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isPinned, setIsPinned] = useState((field as any).display_location === 'profile_header');
  const [pinSaving, setPinSaving] = useState(false);

  const hasValue = displayValue !== undefined && displayValue !== null && displayValue !== '';

  // TRUS-392: a CALCULATED field is filled in and kept current by the system,
  // so it must not offer an edit affordance. The backend drops any value posted
  // for one, which means an editable pencil here would be a control that
  // silently does nothing — worse than no control at all.
  const derived = isDerived(field as any);
  const derivedRule = (field as any).computed_formula || null;

  const togglePinToHeader = async () => {
    setPinSaving(true);
    const newLocation = isPinned ? 'demographics' : 'profile_header';
    try {
      const baseUrl = (productConfig.weaveinApi.baseUrl ?? '').replace(/\/$/, '');
      const token = localStorage.getItem('auth_token') || '';
      const cboId = localStorage.getItem('active_cbo_id') || '';
      const resp = await fetch(`${baseUrl}/api/custom-fields/${field.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, 'X-CBO-ID': cboId },
        body: JSON.stringify({ display_location: newLocation }),
      });
      if (resp.ok) {
        setIsPinned(!isPinned);
        (field as any).display_location = newLocation;
      } else {
        alert('Failed to update display location');
      }
    } catch {
      alert('Failed to update display location');
    } finally {
      setPinSaving(false);
    }
  };
  // --- Field settings (config) editing — parity with Field Configuration (TRUS-242).
  // The Member Profile inline editor previously let staff change only a field's
  // VALUE. Field definition attributes (display name, type, options, required,
  // hidden) were editable only in the Field Configuration admin surface. Both write
  // com_fields, so we expose the same attributes here via the PUT the pin action
  // already uses (/api/custom-fields/{id}); edits stay consistent across both entry
  // points. Conditional-logic (visibility_rules) editing remains in Field
  // Configuration for now.
  const SETTINGS_TYPES: Array<{ value: string; label: string }> = [
    { value: 'str', label: 'Text' },
    { value: 'long_text', label: 'Long text (paragraph)' },
    { value: 'int', label: 'Number' },
    { value: 'bool', label: 'Yes / No' },
    { value: 'date', label: 'Date' },
    { value: 'datetime', label: 'Date & time' },
    { value: 'select', label: 'Dropdown (single)' },
    { value: 'multi_select', label: 'Dropdown (multiple)' },
    { value: 'checkbox', label: 'Checkboxes' },
    { value: 'radio', label: 'Radio buttons' },
    { value: 'toggle', label: 'Toggle' },
  ];
  const SELECT_LIKE = new Set(['select', 'multi_select', 'checkbox', 'radio']);
  const optionsToText = (opts?: FieldOption[] | null) =>
    (opts || []).map((o) => o.label).join('\n');

  const [meta, setMeta] = useState({
    friendly_name: field.friendly_name,
    type: field.type as string,
    mandatory: !!field.mandatory,
    hidden: !!field.hidden,
  });
  const [showSettings, setShowSettings] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsDraft, setSettingsDraft] = useState({
    friendly_name: field.friendly_name,
    type: field.type as string,
    mandatory: !!field.mandatory,
    hidden: !!field.hidden,
    optionsText: optionsToText(field.field_options),
  });

  const openSettings = () => {
    setSettingsError(null);
    setSettingsDraft({
      friendly_name: meta.friendly_name,
      type: meta.type,
      mandatory: meta.mandatory,
      hidden: meta.hidden,
      optionsText: optionsToText(field.field_options),
    });
    setShowSettings(true);
  };

  const saveSettings = async () => {
    const draftOptions: FieldOption[] = settingsDraft.optionsText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((label) => ({ label, value: label }));
    // Mirror the backend guard: a select-like type must have at least one option.
    if (SELECT_LIKE.has(settingsDraft.type) && draftOptions.length === 0) {
      setSettingsError('Add at least one option for this field type.');
      return;
    }
    // Only send changed attributes (exclude_unset on the backend).
    const payload: Record<string, any> = {};
    if (settingsDraft.friendly_name !== meta.friendly_name) payload.friendly_name = settingsDraft.friendly_name;
    if (settingsDraft.type !== meta.type) payload.type = settingsDraft.type;
    if (settingsDraft.mandatory !== meta.mandatory) payload.mandatory = settingsDraft.mandatory;
    if (settingsDraft.hidden !== meta.hidden) payload.hidden = settingsDraft.hidden;
    if (SELECT_LIKE.has(settingsDraft.type) && optionsToText(field.field_options) !== settingsDraft.optionsText) {
      payload.field_options = draftOptions;
    }
    if (Object.keys(payload).length === 0) {
      setShowSettings(false);
      return;
    }
    setSettingsSaving(true);
    setSettingsError(null);
    try {
      const baseUrl = (productConfig.weaveinApi.baseUrl ?? '').replace(/\/$/, '');
      const token = localStorage.getItem('auth_token') || '';
      const cboId = localStorage.getItem('active_cbo_id') || '';
      const resp = await fetch(`${baseUrl}/api/custom-fields/${field.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'X-CBO-ID': cboId },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const errText = await resp.text().catch(() => '');
        setSettingsError(`Failed to save (${resp.status}). ${errText.slice(0, 160)}`);
        return;
      }
      // Reflect locally + mutate the shared field object so the surrounding profile
      // shows the new label/type without a full reload (parity with Field Config).
      if ('friendly_name' in payload) (field as any).friendly_name = payload.friendly_name;
      if ('type' in payload) (field as any).type = payload.type;
      if ('mandatory' in payload) (field as any).mandatory = payload.mandatory;
      if ('hidden' in payload) (field as any).hidden = payload.hidden;
      if ('field_options' in payload) (field as any).field_options = payload.field_options;
      setMeta({
        friendly_name: settingsDraft.friendly_name,
        type: settingsDraft.type,
        mandatory: settingsDraft.mandatory,
        hidden: settingsDraft.hidden,
      });
      setShowSettings(false);
    } catch (e: any) {
      setSettingsError('Failed to save field settings.');
    } finally {
      setSettingsSaving(false);
    }
  };

  const fieldType = field.type;
  const hasOptions = field.field_options && field.field_options.length > 0;
  // Long-text str fields (str_len > 255, e.g. "Survivor Story" with str_len 12000)
  // are edited/displayed as a full-width, multi-row area — matching
  // AddEditMemberNew.renderCustomField, which already renders these as a col-span-12
  // <Textarea rows={4}>. The inline editor previously forced every str field through
  // the single-line ~28px <input>, so a long narrative was only ever visible through
  // a "less than 1 inch" slit (TRUS-296). Parity fix in the spirit of TRUS-289.
  // The explicit `long_text` type always gets the multi-line treatment; plain
  // `str` fields fall back to the str_len cap heuristic (legacy data configured
  // before the long_text type existed).
  const isLongText = fieldType === 'long_text' || (fieldType === 'str' && (field.str_len ?? 0) > 255);

  const handleSave = async () => {
    if (editValue === String(displayValue ?? '')) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onSave(field.name, editValue);
    setSaving(false);
    if (ok) {
      setDisplayValue(editValue);
      setEditing(false);
    }
  };

  const handleCancel = () => {
    setEditValue(String(displayValue ?? ''));
    setEditing(false);
  };

  const loadHistory = async () => {
    if (showHistory) { setShowHistory(false); return; }
    setHistoryLoading(true);
    try {
      const baseUrl = (productConfig.weaveinApi.baseUrl ?? '').replace(/\/$/, '');
      const token = localStorage.getItem('auth_token') || '';
      const cboId = localStorage.getItem('active_cbo_id') || '';
      const resp = await fetch(`${baseUrl}/api/custom-fields/history/field/${field.id}/member/${memberId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': cboId },
      });
      const data = await resp.json();
      setHistory(Array.isArray(data) ? data : []);
      setShowHistory(true);
    } catch {
      setHistory([]);
      setShowHistory(true);
    } finally {
      setHistoryLoading(false);
    }
  };

  const renderInput = () => {
    if (fieldType === 'multi_select') {
      // A multi_select renders as a CHECKBOX group — matching the Add/Edit member form
      // (AddEditMemberNew.renderCustomField) so the same field looks the same on both
      // surfaces (TRUS-289: the profile was showing this field as a plain text box).
      // Note: this branch must NOT be gated on hasOptions — without options it used to
      // fall through to the free-text <input> below, silently turning a constrained
      // multi-select into an arbitrary text field. Value stays a JSON-encoded array.
      const selectedValues: string[] = (() => {
        try {
          const p = JSON.parse(editValue);
          return Array.isArray(p) ? p : [];
        } catch {
          return editValue ? [editValue] : [];
        }
      })();
      if (!hasOptions) {
        return (
          <div className="w-full text-xs text-gray-500 italic border border-blue-200 rounded px-2 py-1 bg-gray-50">
            No options are configured for this field.
          </div>
        );
      }
      const toggleValue = (val: string) => {
        const next = selectedValues.includes(val)
          ? selectedValues.filter((v) => v !== val)
          : [...selectedValues, val];
        setEditValue(JSON.stringify(next));
      };
      return (
        <div className="w-full flex flex-col gap-1 border border-blue-300 rounded px-2 py-1 bg-white max-h-72 overflow-y-auto">
          {orderOptions(field.field_options).map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={selectedValues.includes(opt.value)}
                onChange={() => toggleValue(opt.value)}
                className="rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      );
    }
    if (hasOptions) {
      return (
        <select
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          autoFocus
          className="w-full h-7 text-sm border border-blue-300 rounded px-2 bg-white focus:ring-2 focus:ring-blue-500"
        >
          <option value="">— Select —</option>
          {/* A stored value that is no longer on the list (e.g. a DCM who left) stays
              visible as the current choice instead of the select falling back to
              "— Select —", but cannot be picked again (TRUS-716). */}
          {isOffList(field, displayValue) && (
            <option value={String(displayValue)} disabled>
              {t('members.offListOption', '{{value}} (no longer on the list)', { value: String(displayValue) })}
            </option>
          )}
          {orderOptions(field.field_options).map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    }

    if (fieldType === 'bool' || fieldType === 'toggle' || fieldType === 'checkbox') {
      return (
        <select
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          autoFocus
          className="w-full h-7 text-sm border border-blue-300 rounded px-2 bg-white"
        >
          <option value="">— Select —</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    if (fieldType === 'date' || fieldType === 'fixed_date') {
      return (
        <input
          type="date"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          autoFocus
          className="w-full h-7 text-sm border border-blue-300 rounded px-2"
        />
      );
    }

    if (fieldType === 'int') {
      const isNegative = editValue !== '' && Number(editValue) < 0;
      return (
        <div>
          <input
            type="number"
            min={0}
            value={editValue}
            // Reject negatives: clamp anything below 0 back up to 0.
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') return setEditValue('');
              const n = Number(raw);
              setEditValue(Number.isNaN(n) ? raw : String(Math.max(0, n)));
            }}
            autoFocus
            className="w-full h-7 text-sm border border-blue-300 rounded px-2"
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'Subtract') { e.preventDefault(); return; }
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
          />
          {isNegative && (
            <div className="text-xs text-red-500 mt-1">Value must be 0 or greater.</div>
          )}
        </div>
      );
    }

    if (isLongText) {
      // Multi-row textarea for long narrative text. Enter must insert a newline
      // (not save), so only Escape is bound; save is via the check button.
      const maxLen = field.str_len || 12000;
      return (
        <textarea
          value={editValue}
          onChange={(e) => { setEditValue(e.target.value); autoGrowTextarea(e.target); }}
          ref={autoGrowTextarea}
          autoFocus
          rows={longTextRows(field.str_len)}
          maxLength={maxLen}
          className="w-full text-sm border border-blue-300 rounded px-2 py-1 resize-y min-h-[8rem]"
          onKeyDown={(e) => { if (e.key === 'Escape') handleCancel(); }}
        />
      );
    }

    return (
      <input
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        autoFocus
        className="w-full h-7 text-sm border border-blue-300 rounded px-2"
        onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
      />
    );
  };

  return (
    // Long-text fields span the full grid row (the parent lays fields out in a
    // 2-/4-column grid); this root div is the direct grid child, so col-span here
    // widens the field without the parent needing to know it is long text.
    // min-w-0 lets this grid track shrink below its content width (grid tracks
    // default to min-width:auto); without it an unbreakable value — e.g. a long
    // email in a narrow 1/4 column — overflows into the next cell (TRUS-635).
    <div className={isLongText ? 'min-w-0 sm:col-span-2 md:col-span-4' : 'min-w-0'}>
      {/* Label row */}
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
        <span>{meta.friendly_name}</span>
        {meta.mandatory && <span className="text-red-400">*</span>}
        <div className="ml-auto flex items-center gap-0.5">
          <button
            type="button"
            className={`p-0.5 transition-colors ${showSettings ? 'text-blue-600' : 'text-gray-300 hover:text-blue-500'}`}
            title="Edit field settings (name, type, options, required)"
            onClick={() => (showSettings ? setShowSettings(false) : openSettings())}
          >
            <Settings className="w-3 h-3" />
          </button>
          <button
            type="button"
            className={`p-0.5 transition-colors ${isPinned ? 'text-purple-500 hover:text-purple-700' : 'text-gray-300 hover:text-purple-500'}`}
            title={isPinned ? 'Unpin from profile header' : 'Pin to profile header'}
            onClick={togglePinToHeader}
            disabled={pinSaving}
          >
            {pinSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : isPinned ? <Pin className="w-3 h-3" /> : <PinOff className="w-3 h-3" />}
          </button>
          <button
            type="button"
            className="text-gray-300 hover:text-blue-500 transition-colors p-0.5"
            title="View change history"
            onClick={loadHistory}
          >
            {historyLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />}
          </button>
          {!editing && !derived && (
            <button
              type="button"
              className="text-gray-300 hover:text-blue-500 transition-colors p-0.5"
              title={`Edit ${field.friendly_name}`}
              onClick={() => { setEditValue(String(displayValue ?? '')); setEditing(true); }}
            >
              <Pencil className="w-3 h-3" />
            </button>
          )}
          {derived && (
            <span
              className="text-gray-400 dark:text-gray-500 p-0.5"
              title={describeRule(derivedRule) || 'Calculated automatically'}
              aria-label="Calculated automatically"
            >
              <Calculator className="w-3 h-3" />
            </span>
          )}
        </div>
      </div>

      {/* Field settings (config) panel — parity with Field Configuration (TRUS-242) */}
      {showSettings && (
        <div
          data-testid="field-settings-panel"
          className="mb-2 bg-blue-50 dark:bg-gray-900 rounded border border-blue-200 dark:border-gray-700 p-2 text-xs space-y-2"
        >
          <label className="block">
            <span className="text-gray-500 dark:text-gray-400">Display name</span>
            <input
              type="text"
              aria-label="Display name"
              className="mt-0.5 w-full h-7 border border-gray-300 dark:border-gray-600 rounded px-2 bg-white dark:bg-gray-800"
              value={settingsDraft.friendly_name}
              onChange={(e) => setSettingsDraft((d) => ({ ...d, friendly_name: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-gray-500 dark:text-gray-400">Field type</span>
            <select
              aria-label="Field type"
              className="mt-0.5 w-full h-7 border border-gray-300 dark:border-gray-600 rounded px-2 bg-white dark:bg-gray-800"
              value={settingsDraft.type}
              onChange={(e) => setSettingsDraft((d) => ({ ...d, type: e.target.value }))}
            >
              {SETTINGS_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
              {/* Preserve an unlisted current type (e.g. computed/field_group) so it isn't lost. */}
              {!SETTINGS_TYPES.some((t) => t.value === settingsDraft.type) && (
                <option value={settingsDraft.type}>{settingsDraft.type}</option>
              )}
            </select>
          </label>
          {SELECT_LIKE.has(settingsDraft.type) && (
            <label className="block">
              <span className="text-gray-500 dark:text-gray-400">Options (one per line)</span>
              <textarea
                aria-label="Options"
                className="mt-0.5 w-full min-h-[60px] border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800"
                value={settingsDraft.optionsText}
                onChange={(e) => setSettingsDraft((d) => ({ ...d, optionsText: e.target.value }))}
              />
            </label>
          )}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={settingsDraft.mandatory}
                onChange={(e) => setSettingsDraft((d) => ({ ...d, mandatory: e.target.checked }))}
              />
              <span className="text-gray-600 dark:text-gray-300">Required</span>
            </label>
            <label className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={settingsDraft.hidden}
                onChange={(e) => setSettingsDraft((d) => ({ ...d, hidden: e.target.checked }))}
              />
              <span className="text-gray-600 dark:text-gray-300">Hidden</span>
            </label>
          </div>
          {settingsError && <p className="text-red-500">{settingsError}</p>}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={saveSettings}
              disabled={settingsSaving}
              className="px-2 py-1 bg-v2accent text-white rounded hover:bg-v2accent/90 disabled:opacity-50 flex items-center gap-1"
            >
              {settingsSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              Save settings
            </button>
            <button
              type="button"
              onClick={() => setShowSettings(false)}
              className="px-2 py-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Value / Edit row */}
      {editing ? (
        <div className="flex items-center gap-1">
          <div className="flex-1">{renderInput()}</div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
            title="Save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          </button>
          <button
            onClick={handleCancel}
            className="p-1 text-gray-400 hover:bg-gray-50 rounded transition-colors"
            title="Cancel"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : derived ? (
        /* Calculated: display only. No click-to-edit, no "Click to set" prompt —
           the system owns this value. A list-collect roll-up (e.g. every age in
           the household) renders comma-separated rather than as raw JSON, and an
           empty result renders an em dash instead of inviting an edit. */
        <div
          className={`text-sm flex gap-1 ${isLongText ? 'items-start' : 'items-center min-h-[28px]'}`}
          title={describeRule(derivedRule) || 'Calculated automatically'}
        >
          <span
            className={`font-medium text-gray-700 dark:text-gray-200 ${
              isLongText ? 'whitespace-pre-wrap break-words flex-1' : ''
            }`}
          >
            {formatDerived(displayValue)}
          </span>
        </div>
      ) : (
        <div
          className={`text-sm cursor-pointer group flex gap-1 ${isLongText ? 'items-start' : 'items-center min-h-[28px]'}`}
          onClick={() => { setEditValue(String(displayValue ?? '')); setEditing(true); }}
          title="Click to edit"
        >
          {hasValue ? (
            <span className={`font-medium ${isLongText ? 'whitespace-pre-wrap break-words flex-1' : 'min-w-0 break-words'}`}>
              {(fieldType === 'bool' || fieldType === 'toggle' || fieldType === 'checkbox') ? (
                // Mirror the edit control (Yes/No select) in the display: the
                // stored value is boolean true/false (API) or the string
                // 'true'/'false' (after an inline save), and rendering it raw
                // showed "false" instead of "No" (TRUS-575).
                displayValue === true || displayValue === 'true' ? 'Yes' : 'No'
              ) : fieldType === 'multi_select' ? (() => {
                try {
                  const arr = JSON.parse(String(displayValue));
                  if (Array.isArray(arr) && arr.length > 0) return arr.join(', ');
                } catch {}
                return String(displayValue);
              })() : String(displayValue)}
            </span>
          ) : (
            <span className="text-gray-300 italic">Click to set</span>
          )}
          {needsReassignment(field, displayValue, recordValues) && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 whitespace-nowrap"
              title={t(
                'members.needsReassignmentHint',
                'This open case is assigned to someone who is no longer on the list. Choose a current assignee.',
              )}
            >
              <AlertTriangle className="w-3 h-3" />
              {t('members.needsReassignment', 'Needs reassignment')}
            </span>
          )}
          <Pencil className={`w-3 h-3 text-gray-200 group-hover:text-blue-400 transition-colors ${isLongText ? 'mt-0.5 flex-shrink-0' : ''}`} />
        </div>
      )}

      {/* History panel */}
      {showHistory && (
        <div className="mt-1 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 p-2 text-xs max-h-32 overflow-y-auto">
          {history.length === 0 ? (
            <p className="text-gray-400 italic">No change history recorded.</p>
          ) : (
            <div className="space-y-1">
              {history.map((h: any, i: number) => {
                const localDate = h.changed_at ? new Date(h.changed_at).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '?';
                return (
                  <div key={i} className="flex items-start gap-2">
                    <span className="text-gray-400 whitespace-nowrap">{localDate}</span>
                    <span>
                      <span className="text-red-400 line-through">{h.old_value || '(empty)'}</span>
                      {' → '}
                      <span className="text-green-600 font-medium">{h.new_value || '(empty)'}</span>
                    </span>
                    <span className="text-gray-400 ml-auto">{h.changed_by_name || ''}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
